import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VideocamIcon from "@mui/icons-material/VideocamOutlined";
import { useEffect, useState } from "react";
import type { VideoCallSeatSummary } from "@shared/types/foyer";
import { foyerApi, type LicenseRecord } from "../http/foyer";
import { PermissionGate, usePermission } from "../auth/access";
import { ItemFormSurface, type Dismiss } from "./EditDrawer";
import { describeError, useToast } from "./toast";
import { useTranslation } from "react-i18next";
import { errorText } from "../utils/apiError";
import type { Translate } from "../i18n/translate";

/** The `product` value that models the pool. Mirrors the server's
 *  `VIDEO_CALL_PRODUCT` (src/server/db/models/License.ts). */
export const VIDEO_CALL_PRODUCT = "video-call";

interface Props {
  premiseId: string;
  /**
   * The premise's licenses, as already loaded by the parent card. Passed in
   * rather than re-fetched so the panel and the table below it can never
   * disagree about which `video-call` rows exist - and so a new array identity
   * (the parent's post-mutation refresh) re-reads the seat summary, which any
   * license change can move.
   */
  licenses: LicenseRecord[] | null;
  /** Re-load the parent's licenses (and, through them, this panel). */
  onChanged: () => Promise<void>;
}

/**
 * The video-call seat pool. `product` is free text: a typo here creates a
 * licence that silently entitles nobody, so the product is fixed in code.
 */
export default function VideoCallSeatPoolPanel({
  premiseId,
  licenses,
  onChanged,
}: Props) {
  const { t } = useTranslation(["care", "common"]);
  const canManage = usePermission("video-calls", "administrate", {
    premiseId,
  });
  const [summary, setSummary] = useState<VideoCallSeatSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const sum = await foyerApi.videoCallSeatSummary(premiseId);
        if (!alive) return;
        // Defensive: a mocked test fetch might return a non-object.
        setSummary(sum && typeof sum === "object" ? sum : null);
        setErr(null);
      } catch (e) {
        if (!alive) return;
        setErr(errorText(e, t));
      }
    })();
    return () => {
      alive = false;
    };
    // `licenses` is the parent's refresh signal - see the prop docs.
  }, [premiseId, licenses, t]);

  // Every video-call row, and the subset the pool actually counts. A premise
  // may legitimately hold several (two purchase orders), and a row parked as
  // `pending`/`cancelled` contributes nothing - both facts change what we can
  // offer to edit, so keep them apart.
  const rows = (licenses ?? []).filter((l) => l.product === VIDEO_CALL_PRODUCT);
  const activeRows = rows.filter((l) => l.status === "active");
  // Only offer in-place editing when there is exactly one row to edit. With
  // several, "change the pool size" has no single answer, so we send the admin
  // to the table rather than silently picking a row.
  const editable = rows.length === 1 ? rows[0] : null;

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        borderRadius: 2,
        bgcolor: "action.hover",
      }}
      data-testid="video-call-pool"
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
        >
          <VideocamIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">
            {t("videoCallSeatPoolPanel.title")}
          </Typography>
          {summary && (
            <PermissionGate
              resource="video-calls"
              level="view"
              place={{ premiseId }}
            >
              <SeatChips summary={summary} />
            </PermissionGate>
          )}
        </Stack>

        {canManage &&
          (editable || rows.length === 0 ? (
            <Button
              size="small"
              variant={
                summary?.poolSeats || summary?.unlimited ? "text" : "outlined"
              }
              onClick={() => setDialogOpen(true)}
              data-testid="video-call-pool-edit"
            >
              {rows.length === 0
                ? t("videoCallSeatPoolPanel.buySeats")
                : t("videoCallSeatPoolPanel.changePoolSize")}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t("videoCallSeatPoolPanel.severalLicenses", {
                count: rows.length,
              })}
            </Typography>
          ))}
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        {summaryCopy(t, summary, rows.length, activeRows.length)}
      </Typography>

      {err && (
        <Typography variant="caption" color="error" sx={{ display: "block" }}>
          {err}
        </Typography>
      )}

      {dialogOpen && (
        <PoolDialog
          premiseId={premiseId}
          record={editable}
          assignedFromPool={summary?.assignedFromPool ?? 0}
          onClose={() => setDialogOpen(false)}
          // Runs after the surface has slid out (it dismisses itself first).
          onSaved={async () => {
            setDialogOpen(false);
            await onChanged();
          }}
        />
      )}
    </Box>
  );
}

/** "3 / 10 assigned" + the family-funded seats that sit outside the pool. */
function SeatChips({ summary }: { summary: VideoCallSeatSummary }) {
  const { t } = useTranslation("care");
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Chip
        size="small"
        variant="outlined"
        color={
          summary.unlimited
            ? "success"
            : summary.hasFreeSeats
              ? "primary"
              : "error"
        }
        label={
          summary.unlimited
            ? t("videoCallSeatPoolPanel.assignedUnlimited", {
                count: summary.assignedFromPool,
              })
            : t("videoCallSeatPoolPanel.assigned", {
                assigned: summary.assignedFromPool,
                total: summary.poolSeats,
              })
        }
        data-testid="video-call-pool-summary"
      />
      {!summary.unlimited && summary.poolSeats > 0 && (
        <Chip
          size="small"
          variant="outlined"
          label={t("videoCallSeatPoolPanel.free", {
            count: summary.freeSeats,
          })}
          data-testid="video-call-pool-free"
        />
      )}
      {summary.familySeats > 0 && (
        <Tooltip title={t("videoCallSeatPoolPanel.familyHint")}>
          <Chip
            size="small"
            variant="outlined"
            label={t("videoCallSeatPoolPanel.familyFunded", {
              count: summary.familySeats,
            })}
            data-testid="video-call-pool-family"
          />
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * One line of plain language under the chips. The empty and the
 * bought-but-not-active states are the two an operator can otherwise stare at
 * for a long time: both look like "video calling is broken" from the TV, and
 * neither is fixed on the TV.
 */
function summaryCopy(
  t: Translate,
  summary: VideoCallSeatSummary | null,
  rowCount: number,
  activeRowCount: number
): string {
  if (!summary) return t("care:videoCallSeatPoolPanel.summary.loading");
  if (summary.unlimited) {
    return t("care:videoCallSeatPoolPanel.summary.unlimited");
  }
  if (rowCount > 0 && activeRowCount === 0) {
    return summary.familySeats > 0
      ? t("care:videoCallSeatPoolPanel.summary.inactiveFamily")
      : t("care:videoCallSeatPoolPanel.summary.inactive");
  }
  if (summary.poolSeats === 0) {
    return summary.familySeats > 0
      ? t("care:videoCallSeatPoolPanel.summary.noneFamily")
      : t("care:videoCallSeatPoolPanel.summary.none");
  }
  if (!summary.hasFreeSeats) {
    return t("care:videoCallSeatPoolPanel.summary.full");
  }
  return t("care:videoCallSeatPoolPanel.summary.assign");
}

/**
 * Buy (a modal) or resize (a right-hand drawer) the pool. Writes the same License row the generic dialog
 * writes, with `product` pinned to `video-call` and the quantity framed as
 * residents rather than screens.
 */
function PoolDialog({
  premiseId,
  record,
  assignedFromPool,
  onClose,
  onSaved,
}: {
  premiseId: string;
  record: LicenseRecord | null;
  assignedFromPool: number;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const { t } = useTranslation(["care", "common"]);
  const [unlimited, setUnlimited] = useState(
    record != null && record.quantity == null
  );
  const [seats, setSeats] = useState(
    record?.quantity != null ? String(record.quantity) : ""
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const parsed = Number(seats);
  const seatsValid =
    unlimited ||
    (seats.trim() !== "" && Number.isInteger(parsed) && parsed >= 0);
  // Shrinking below what's already handed out is allowed on purpose: the store
  // never strands an assigned resident (VC2-4), it just refuses NEW draws. Say
  // so, so nobody assumes seats were revoked - or that the save failed.
  const shrinksBelowAssigned =
    !unlimited && seatsValid && parsed < assignedFromPool;

  async function save(dismiss: Dismiss) {
    setBusy(true);
    setErr(null);
    try {
      const quantity = unlimited ? null : parsed;
      if (record) {
        // Re-activating is part of resizing: an admin who sets a seat count on
        // a parked license means for those seats to exist.
        await foyerApi.updateLicense(record.id, {
          product: VIDEO_CALL_PRODUCT,
          quantity,
          status: "active",
        });
      } else {
        await foyerApi.createLicense({
          premiseId,
          product: VIDEO_CALL_PRODUCT,
          status: "active",
          quantity,
          notes: null,
        });
      }
      toast.success(
        unlimited
          ? t("videoCallSeatPoolPanel.savedUnlimited")
          : t("videoCallSeatPoolPanel.saved", { count: parsed })
      );
      dismiss(() => void onSaved());
    } catch (e) {
      setErr(errorText(e, t));
      toast.error(describeError(t, "saveSeatPool", e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ItemFormSurface
      mode={record ? "edit" : "create"}
      onClose={onClose}
      maxWidth="xs"
      title={
        record
          ? t("videoCallSeatPoolPanel.titleEdit")
          : t("videoCallSeatPoolPanel.titleBuy")
      }
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void save(dismiss)}
            disabled={busy || !seatsValid}
            data-testid="video-call-pool-save"
          >
            {busy ? t("common:states.saving") : t("common:buttons.save")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t("videoCallSeatPoolPanel.intro")}
        </Typography>
        <TextField
          label={t("videoCallSeatPoolPanel.seats")}
          type="number"
          value={unlimited ? "" : seats}
          onChange={(e) => setSeats(e.target.value)}
          disabled={unlimited}
          size="small"
          autoFocus
          inputProps={{
            min: 0,
            step: 1,
            "data-testid": "video-call-pool-seats",
          }}
          helperText={
            unlimited
              ? t("videoCallSeatPoolPanel.helpUnlimited")
              : t("videoCallSeatPoolPanel.helpCount")
          }
        />
        <FormControlLabel
          control={
            <Switch
              checked={unlimited}
              onChange={(e) => setUnlimited(e.target.checked)}
              inputProps={
                { "data-testid": "video-call-pool-unlimited" } as Record<
                  string,
                  string
                >
              }
            />
          }
          label={t("videoCallSeatPoolPanel.siteLicenseUnlimited")}
        />
        {shrinksBelowAssigned && (
          <Alert severity="info" data-testid="video-call-pool-shrink-warning">
            {t("videoCallSeatPoolPanel.shrink", { count: assignedFromPool })}
          </Alert>
        )}
        {err && (
          <Typography variant="caption" color="error">
            {err}
          </Typography>
        )}
      </Stack>
    </ItemFormSurface>
  );
}
